// src/pages/_home/manage-consumers/view-account-ledger/$id.tsx
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DataTable } from "@/components/data-table";
import { YearRangePicker } from "@/components/year-range-picker";
import { Header } from "@/components/header";
import { Button } from "@/components/ui/button";
import { consumerLedgerColumns } from "@/components/table/columns/consumer-ledger-columns";


import { getConsumer } from "@/lib/api/billing";
import { getLedgerPerConsumer, type LedgerEntryType } from "@/lib/api/ledger";
import type { ConsumerInfoType } from "@/types";

export const Route = createFileRoute("/_home/manage-consumers/view-account-ledger/$id")({
  loader: async ({ params: { id } }) => ({ id }),
  component: ViewLedgerPage,
});

function ViewLedgerPage() {
  const { id } = Route.useLoaderData();
  const consumerId = Number(id);
  const navigate = useNavigate();

  const [selectedYears, setSelectedYears] = React.useState<string[]>([]);
  const [pageSize, setPageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");

  const { data: consumerInfo } = useQuery<ConsumerInfoType>({
    queryKey: ["consumerInfo", consumerId],
    queryFn: () => getConsumer(consumerId),
    staleTime: 1000 * 60 * 5,
  });

  const [fromIso, toIso] = React.useMemo(() => {
    if (selectedYears.length >= 2) {
      const [y1, y2] = selectedYears.map(Number).sort((a, b) => a - b);
      const from = new Date(y1, 0, 1);
      const to = new Date(y2, 11, 31, 23, 59, 59);
      return [from.toISOString(), to.toISOString()];
    }
    if (selectedYears.length === 1) {
      const year = Number(selectedYears[0]);
      const from = new Date(year, 0, 1);
      const to = new Date(year, 11, 31, 23, 59, 59);
      return [from.toISOString(), to.toISOString()];
    }
    const to = new Date();
    const from = new Date(to.getTime() - 1000 * 60 * 60 * 24 * 30);
    return [from.toISOString(), to.toISOString()];
  }, [selectedYears]);

  const { data, isLoading, isError } = useQuery<{
    results: LedgerEntryType[];
    count: number;
    next: string | null;
    previous: string | null;
  }>({
    queryKey: ["consumerLedger", consumerId, fromIso, toIso, pageSize, page, search],
    queryFn: () =>
      getLedgerPerConsumer(consumerId, fromIso, toIso, pageSize, page, search),
    staleTime: 1000 * 60 * 5,
  });

  const filteredData = React.useMemo(() => {
    if (!data?.results || selectedYears.length === 0) return data?.results || [];

    return data.results.filter(entry =>
      selectedYears.includes(new Date(entry.trans_date).getFullYear().toString())
    );
  }, [data?.results, selectedYears]);

  return (
    <main className="space-y-4 p-4">
      <div className="flex justify-between items-center">
        <Header
          subroutes={[{ route: "/manage-consumers", label: "Manage Consumers" }]}
        >
          {`Account Ledger (${consumerInfo?.full_name ?? `#${consumerId}`})`}
        </Header>
      </div>

      {isError ? (
        <p className="text-center text-red-500">Failed to load ledger.</p>
      ) : (
        <DataTable
          columns={consumerLedgerColumns}
          data={filteredData}
          isLoading={isLoading}
          totalCount={data?.count}
          nextPageUrl={data?.next}
          prevPageUrl={data?.previous}
          page={page}
          setPage={setPage}
          defaultPage={pageSize}
          setDefaultPage={setPageSize}
          search={{ label: "Search…", value: search, onChange: setSearch }}
        >
          <div className="hidden md:flex ml-auto gap-2 items-center">
            <Button
              onClick={() =>
                navigate({
                  to: "/manage-consumers/add-account-transaction/$id",
                  params: { id: String(consumerId) }, // must match the loader param
                })
              }
            >
              Add Account Transactions
            </Button>
            <div className="w-[200px]">
              <YearRangePicker
                selectedYears={selectedYears}
                setSelectedYears={setSelectedYears}
              />
            </div>
          </div>
        </DataTable>
      )}
    </main>
  );
}

export default ViewLedgerPage;
